import type React from 'react'

import { renderToStaticMarkup } from 'react-dom/server'
import { IntlProvider } from 'react-intl'

import type { AllListingsFilters } from '@b-stock/search-api-client'

import { localeData } from '@/locale/localeData'

import type { ListingFiltersState } from '../ListingSearchProvider'
import { hasCategorySelection } from '../ListingSearchProvider/categoryThreshold'
import { formatKeysToNames } from '../SearchFilters/shared/regionFilterUtils'
import toDisplayNames from '../SearchFilters/toDisplayNames'

const getTextFromJSX = (jsx: React.ReactNode): string => {
  const wrapped = (
    <IntlProvider locale="en" messages={localeData.en}>
      {jsx}
    </IntlProvider>
  )
  const html = renderToStaticMarkup(wrapped)
  const div = document.createElement('div')
  div.innerHTML = html
  return div.textContent || ''
}

const filtersToStrings = (
  filters: Partial<ListingFiltersState>,
  allFilters: AllListingsFilters
): string[] => {
  const { q, ...rest } = filters

  const orderedFilters = typeof q === 'string' ? { q, ...rest } : rest

  return Object.entries(orderedFilters).flatMap(([untypedKey, value]) => {
    const key = untypedKey as keyof ListingFiltersState
    if (Array.isArray(value)) {
      const areStringValues = value.every((v) => typeof v === 'string')

      if (value.every((v) => v === null)) {
        return []
      } else if (key === 'region' && areStringValues) {
        const formatted = formatKeysToNames(value, allFilters.region)
        return formatted.length > 0 ? formatted : value
      } else if (areStringValues) {
        const toDisplayName =
          key in toDisplayNames
            ? (toDisplayNames[key as keyof typeof toDisplayNames] as (
                value: string
              ) => React.JSX.Element)
            : undefined

        return value.map((v) =>
          toDisplayName ? getTextFromJSX(toDisplayName(v)) : v
        )
      } else {
        let prefix: string
        if (key === 'currentBidRanges' || key === 'msrp') {
          // Currency ranges are (currently) only USD
          prefix = '$'
        } else {
          // All other ranges are just numbers
          prefix = ''
        }

        const [lower, upper] = value

        const lowerString = `${prefix}${(lower ?? 0).toLocaleString()}`

        let rangeString: string
        if (upper) {
          rangeString = `${lowerString}-${prefix}${upper.toLocaleString()}`
        } else {
          rangeString = `${lowerString}+`
        }

        // Add label prefix for msrp range
        if (key === 'msrp') {
          return [`MSRP: ${rangeString}`]
        }

        return [rangeString]
      }
    } else if (key === 'q' && typeof value === 'string') {
      return [`"${value}"`]
    } else if (
      key === 'distanceRange' &&
      typeof value === 'object' &&
      value !== null &&
      !Object.values(value).some((v) => v === null)
    ) {
      const toDisplayName = toDisplayNames[key]
      return getTextFromJSX(toDisplayName(value))
    } else if (key === 'msrpPercentage' && typeof value === 'number') {
      return [`MSRP: ${Math.round(value * 10000) / 100}%`]
    } else if (key === 'problemFreeOrderRate' && typeof value === 'number') {
      return [`Facility Score: ${value}%`]
    } else if (
      key === 'categoryThreshold' &&
      typeof value === 'number' &&
      hasCategorySelection(filters)
    ) {
      // Mirrors formatFilters: the threshold only applies alongside a category.
      return [`≥${value}% of lot`]
    } else if (key === 'starterLots' && value === true) {
      return ['Starter Lots']
    } else if (key === 'newSeller' && value === true) {
      return ['New Sellers']
    } else if (key === 'isCreditCardEnabled' && value === true) {
      return ['Accepts Credit Card']
    } else if (key === 'eligibleOnly' && value === true) {
      return ['Eligible Only']
    }

    return []
  })
}

export default filtersToStrings
